import React, { useEffect, useMemo, useState } from 'react'
import { useQuery } from 'react-query'
import DimmingProfileEditor, {
  ActionWrapper,
  Calendar,
  CalendarEditor,
  DALI_ALL_DEVICES,
  DimmingProfile,
  DimmingProfileDraft,
  emptyCalendar,
  isCalendarEmpty,
  LinkedMinigridCoords,
} from '../../dimmingProfile'
import minigridAPI from '../../minigrid/service/minigridAPI'
import { Fleet } from '../types/fleet.types'
import { useFleetProfile, useSaveProfile } from '../hooks/useFleets'
import { usePushFleet } from '../hooks/useFleetDivergence'
import { FleetConflictError } from '../service/fleetAPI'
import { FleetUiStrings } from '../i18n/fleetI18n'
import { useToast } from '../../../common/providers/ToastProvider/useToast'
import ButtonV2 from '../../../common/components/atom/ButtonV2/Button'
import { PermissionTooltip, useCapability } from '../../rbac'

interface FleetProfileSectionProps {
  fleet: Fleet
  strings: FleetUiStrings
}

const NO_PROFILE = 255

// Infobulle « Action non autorisée » sur les boutons d'édition du profil et du
// calendrier (ajout/suppression de palier, exemple) sans `fleet.edit`.
const FleetEditAction: ActionWrapper = ({ children }) => {
  const canEdit = useCapability('fleet.edit')
  return <PermissionTooltip allowed={canEdit}>{children}</PermissionTooltip>
}

// Builds a default empty draft (all devices, ALL days active = 0xFF, no steps).
// 0xFF = all 8 bits set: holiday + Mon..Sun. Existing saved profiles keep their
// stored value; this default only applies to freshly-created / null profiles.
const emptyDraft = (): DimmingProfileDraft => ({
  steps: [],
  active_days: 0xff,
  dali_address: DALI_ALL_DEVICES,
})

// Small priority badge (the control-priority rank from the Luminaire control
// chart — see ControlPrioritiesPanel). Calendar = 3, Profile = 5.
const PriorityCircle: React.FC<{ n: number }> = ({ n }) => (
  <span
    style={{
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: 22,
      height: 22,
      borderRadius: '50%',
      background: '#eef2f7',
      color: '#374151',
      fontSize: 12,
      fontWeight: 600,
      flex: '0 0 auto',
    }}
  >
    {n}
  </span>
)

// Collapsible section so each configurable control source (Calendar, Profile)
// can be folded away; ordered by control priority in the render below.
const AccordionSection: React.FC<{
  priority: number
  title: string
  meta?: React.ReactNode
  open: boolean
  onToggle: () => void
  children: React.ReactNode
}> = ({ priority, title, meta, open, onToggle, children }) => (
  <div
    style={{ border: '1px solid #e5e7eb', borderRadius: 8, overflow: 'hidden' }}
  >
    <button
      type="button"
      onClick={onToggle}
      aria-expanded={open}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        width: '100%',
        padding: '10px 12px',
        background: open ? '#f9fafb' : '#fff',
        border: 'none',
        borderBottom: open ? '1px solid #e5e7eb' : 'none',
        cursor: 'pointer',
        textAlign: 'left',
      }}
    >
      <PriorityCircle n={priority} />
      <span style={{ flex: 1, fontWeight: 600, fontSize: 14, color: '#111827' }}>
        {title}
      </span>
      {meta}
      <span
        style={{
          transform: open ? 'rotate(90deg)' : 'none',
          transition: 'transform 0.15s ease',
          color: '#6b7280',
          fontSize: 12,
        }}
      >
        ▶
      </span>
    </button>
    {open && <div style={{ padding: 12 }}>{children}</div>}
  </div>
)

// Embeds the DimmingProfileEditor for a fleet's profile and persists it via the
// dimmingProfiles endpoint (plan §4 F2). The backend owns version++/CAS.
const FleetProfileSection: React.FC<FleetProfileSectionProps> = ({
  fleet,
  strings,
}) => {
  const profileQuery = useFleetProfile(fleet.fleet_id, fleet.profile_id)
  const saveMutation = useSaveProfile()
  // RBAC : édition du profil / calendrier = fleet.edit (editor+). Sans ce droit
  // (ou tant que le rôle n'est pas connu), l'éditeur passe en lecture seule.
  const canEdit = useCapability('fleet.edit')
  const pushMutation = usePushFleet()
  const toast = useToast()
  const [draft, setDraft] = useState<DimmingProfileDraft>(emptyDraft)
  // 0x20 zenith calendar lives in the SAME profile document. Start from defaults
  // (clamps_profiles=true); only send a calendar when the operator fills it.
  const [calendar, setCalendar] = useState<Calendar>(emptyCalendar)
  // The two configurable control sources are shown as accordions, ordered by
  // control priority (Calendar = priority 3 above Profile = priority 5).
  const [openCalendar, setOpenCalendar] = useState(true)
  const [openProfile, setOpenProfile] = useState(true)

  // Resolve the FIRST linked minigrid's coordinates so the calendar can prefill
  // latitude/longitude from it (the device sits on that minigrid). Cached and
  // shared with the other minigrid queries via the same query key.
  const minigridsQuery = useQuery({
    queryKey: ['minigrids', 'all'],
    queryFn: () => minigridAPI.getAllMinigrids(0, 0),
    staleTime: 30_000,
  })
  const linkedMinigrid = useMemo<LinkedMinigridCoords | null>(() => {
    const firstId = fleet.minigrid_ids?.[0]
    if (!firstId) return null
    const m = (minigridsQuery.data?.data ?? []).find(
      (mg) => mg.minigrid_id === firstId,
    )
    if (!m) return null
    return {
      lat: m.minigrid_location_lat,
      lng: m.minigrid_location_lng,
      minigridName: m.minigrid_name,
    }
  }, [fleet.minigrid_ids, minigridsQuery.data])

  // Seed the local draft from the fetched profile (or empty for a new one).
  useEffect(() => {
    if (profileQuery.data) {
      setDraft({
        steps: profileQuery.data.steps ?? [],
        active_days: profileQuery.data.active_days ?? 0xff,
        dali_address: profileQuery.data.dali_address ?? DALI_ALL_DEVICES,
      })
      setCalendar(profileQuery.data.calendar ?? emptyCalendar())
    } else {
      setDraft(emptyDraft())
      setCalendar(emptyCalendar())
    }
  }, [profileQuery.data])

  // FIX D: active_days === 0 means no day selected → block save.
  const noDaysSelected = draft.active_days === 0

  const handleSave = () => {
    if (noDaysSelected || !canEdit) return
    const existing = profileQuery.data
    const profile: DimmingProfile = {
      profile_id: fleet.profile_id,
      fleet_id: fleet.fleet_id,
      version: existing?.version ?? 0,
      steps: draft.steps,
      active_days: draft.active_days,
      dali_address: draft.dali_address,
      // Only attach the calendar when the operator actually filled it in, so an
      // empty editor leaves the fleet with "no calendar".
      ...(isCalendarEmpty(calendar) ? {} : { calendar }),
    }
    saveMutation.mutate(
      { profile },
      {
        onSuccess: () => {
          toast.success(strings.save)
          // FIX E: platform change → auto-push so momis get the new profile
          // immediately. Best-effort: toast on failure but don't block the UI.
          pushMutation.mutate(
            { fleetId: fleet.fleet_id },
            {
              onSuccess: (results) => {
                // Aucune cible (flotte sans minigrid lié) : le push n'a atteint
                // aucune lampe → retour d'info explicite plutôt qu'un silence.
                if (results.length === 0) {
                  toast.info(strings.noPushTarget)
                  return
                }
                const failed = results.filter((r) => !r.pushed)
                if (failed.length > 0) {
                  toast.error(`${strings.failed}: ${failed.length}/${results.length}`)
                }
              },
              onError: () => toast.error(strings.genericError),
            },
          )
        },
        onError: (err) => {
          if (err instanceof FleetConflictError) {
            toast.error(strings.conflictError)
            profileQuery.refetch()
            return
          }
          toast.error(strings.genericError)
        },
      },
    )
  }

  const profileMeta =
    fleet.profile_id !== NO_PROFILE ? (
      <span style={{ fontSize: 12, color: '#6b7280', fontWeight: 400 }}>
        profile_id {fleet.profile_id} · {strings.version}{' '}
        {profileQuery.data?.version ?? fleet.version}
      </span>
    ) : null

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {/* Configurable control sources, ordered by control priority:
          Calendar (priority 3) sits above Profile (priority 5). */}
      <AccordionSection
        priority={3}
        title={strings.cpCalendarName}
        open={openCalendar}
        onToggle={() => setOpenCalendar((v) => !v)}
      >
        <CalendarEditor
          value={calendar}
          onChange={setCalendar}
          linkedMinigrid={linkedMinigrid}
          readOnly={!canEdit}
          actionWrapper={FleetEditAction}
        />
      </AccordionSection>

      <AccordionSection
        priority={5}
        title={strings.profile}
        meta={profileMeta}
        open={openProfile}
        onToggle={() => setOpenProfile((v) => !v)}
      >
        <DimmingProfileEditor
          value={draft}
          onChange={setDraft}
          readOnly={!canEdit}
          actionWrapper={FleetEditAction}
        />
      </AccordionSection>

      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <PermissionTooltip allowed={canEdit}>
          <ButtonV2
            type="button"
            onClick={handleSave}
            disabled={!canEdit || saveMutation.isLoading || noDaysSelected}
            variant="contained"
            color="primary"
            size="medium"
          >
            {saveMutation.isLoading ? strings.saving : strings.save}
          </ButtonV2>
        </PermissionTooltip>
        {noDaysSelected && (
          <span style={{ fontSize: 12, color: '#b91c1c' }}>
            {strings.noDaysSelected}
          </span>
        )}
      </div>
    </div>
  )
}

export default FleetProfileSection
